import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Plus, Search, Pencil, Trash2 } from 'lucide-react';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import { usePermissionData } from 'hooks/usePermissionData';
import { Permission } from 'types/permission';
import AddPermissionModal from './AddPermissionModal';
import EditPermissionModal from './EditPermissionModal';
import DeletePermissionModal from './DeletePermissionModal';
import styles from './Permission.module.scss';

const PermissionPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [permissionToEdit, setPermissionToEdit] = useState<Permission | null>(
    null,
  );
  const [permissionToDelete, setPermissionToDelete] =
    useState<Permission | null>(null);

  const {
    permissions,
    loading,
    reloading,
    error,
    reload,
    addPermission,
    updatePermission,
    removePermission,
  } = usePermissionData();

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredPermissions = useMemo(() => {
    const searchLower = debouncedSearchQuery.toLowerCase();
    return permissions.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        p.key.toLowerCase().includes(searchLower) ||
        (p.description?.toLowerCase().includes(searchLower) ?? false),
    );
  }, [permissions, debouncedSearchQuery]);

  const paginatedPermissions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredPermissions.slice(startIndex, startIndex + pageSize);
  }, [filteredPermissions, currentPage, pageSize]);

  const handleAddSuccess = (permission: Permission) => {
    addPermission(permission);
    setIsAddModalOpen(false);
    showToast('Permission created successfully', 'success');
  };

  const handleEditSuccess = (permission: Permission) => {
    updatePermission(permission);
    setPermissionToEdit(null);
    showToast('Permission updated successfully', 'success');
  };

  const handleDeleteSuccess = (id: string) => {
    removePermission(id);
    setPermissionToDelete(null);
    showToast('Permission deleted successfully', 'success');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Permissions</h1>
        <div className={styles.headerButtons}>
          <RefreshButton onClick={reload} loading={reloading} />
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus size={18} style={{ marginRight: '0.5rem' }} />
            New Permission
          </Button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchContainer}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search permissions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.loadingText}>Loading permissions...</div>
        </div>
      ) : error ? (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={reload}>Retry</Button>
        </div>
      ) : permissions.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No permissions found. Create one to get started.</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          {reloading && (
            <div className={styles.loadingOverlay}>
              <span>Reloading...</span>
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th className={styles.desktopOnlyCell}>Description</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPermissions.map((permission) => (
                <tr key={permission.id}>
                  <td className={styles.nameCell}>
                    <div>{permission.name}</div>
                    <div className={styles.mobileDescription}>
                      {permission.description?.trim() || '-'}
                    </div>
                  </td>
                  <td>
                    <span className={styles.keyCell}>{permission.key}</span>
                  </td>
                  <td
                    className={`${styles.descriptionCell} ${styles.desktopOnlyCell}`}
                  >
                    {permission.description || '-'}
                  </td>
                  <td className={styles.actionsCell}>
                    <button
                      className={`${styles.actionButton} ${styles.editButton}`}
                      onClick={() => setPermissionToEdit(permission)}
                      title="Edit Permission"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.deleteButton}`}
                      onClick={() => setPermissionToDelete(permission)}
                      title="Delete Permission"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredPermissions.length > 0 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredPermissions.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <AddPermissionModal
        show={isAddModalOpen}
        onHide={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
        onError={(msg) => showToast(msg, 'error')}
      />

      <EditPermissionModal
        show={!!permissionToEdit}
        onHide={() => setPermissionToEdit(null)}
        permission={permissionToEdit}
        onSuccess={handleEditSuccess}
        onError={(msg) => showToast(msg, 'error')}
      />

      <DeletePermissionModal
        show={!!permissionToDelete}
        onHide={() => setPermissionToDelete(null)}
        permission={permissionToDelete}
        onSuccess={handleDeleteSuccess}
        onError={(msg) => showToast(msg, 'error')}
      />
    </div>
  );
};

export default PermissionPage;
